"use client";

import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { ar, fr as frLocale } from "date-fns/locale";
import { CalendarIcon, ChevronLeft, ChevronRight, Clock, CalendarDays, UserCircle, Check, ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTableFacetedFilter } from "@/views/users/components/data-table-faceted-filter";
import { cabinet_case_type } from "@/i18n";
import { CaseType } from "@/views/cases/data/schema";
import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { getMonthNames } from "@/utils/date-locale";
import { get_users } from "@/api/user";
import { User } from "@/views/users/data/schema";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface CalendarHeaderProps {
  view: "month" | "week" | "day";
  setView: (view: "month" | "week" | "day") => void;
  selectedDay: Date;
  monthDisplay: string;
  currentDate: Date;
  goToToday: () => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  goToDate: (date: Date) => void;
  selectedCaseTypes?: string[];
  onCaseTypeFilterChange?: (values: string[]) => void;
  selectedAssignedTo?: string;
  onAssignedToFilterChange?: (value: string | undefined) => void;
}

export function CalendarHeader({
  view,
  setView,
  selectedDay,
  monthDisplay,
  currentDate,
  goToToday,
  goToPreviousMonth,
  goToNextMonth,
  goToPreviousWeek,
  goToNextWeek,
  goToPreviousDay,
  goToNextDay,
  goToDate,
  selectedCaseTypes,
  onCaseTypeFilterChange,
  selectedAssignedTo,
  onAssignedToFilterChange,
}: CalendarHeaderProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const currentLocale = i18n.language === 'ar' ? ar : frLocale;
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState<number>(currentDate.getFullYear());

  // Lawyer filter state
  const [lawyerOpen, setLawyerOpen] = useState(false);
  const [lawyers, setLawyers] = useState<User[]>([]);
  const [lawyerSearch, setLawyerSearch] = useState("");
  const [lawyersLoading, setLawyersLoading] = useState(false);
  const lawyerSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedLawyer = useMemo(
    () => lawyers.find((u) => u.id === selectedAssignedTo) ?? null,
    [lawyers, selectedAssignedTo]
  );

  // Fetch lawyers when dropdown opens or search changes
  useEffect(() => {
    if (!lawyerOpen) return;
    if (lawyerSearchTimeout.current) clearTimeout(lawyerSearchTimeout.current);
    lawyerSearchTimeout.current = setTimeout(() => {
      setLawyersLoading(true);
      get_users({ roles: null, is_active: null, ordering: null, page: 0, page_size: 30, name: lawyerSearch || null })
        .then((res) => setLawyers(res.results))
        .finally(() => setLawyersLoading(false));
    }, 250);
    return () => {
      if (lawyerSearchTimeout.current) clearTimeout(lawyerSearchTimeout.current);
    };
  }, [lawyerOpen, lawyerSearch]);

  const monthNames = useMemo(() => {
    return getMonthNames(i18n.language as 'ar' | 'fr');
  }, [i18n.language]);

  const yearOptions = useMemo(() => {
    const base = currentDate.getFullYear();
    return Array.from({ length: 11 }, (_, i) => base - 5 + i);
  }, [currentDate]);
  const flexDirection = isRTL ? 'flex-row-reverse' : 'flex-row';
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-gray-200 bg-white dark:bg-gray-800 px-3 py-3 md:px-4 mb-4">
      {/* Top row on mobile: Today button and View toggles */}
      <div className="flex items-center justify-between md:justify-start gap-2">
        <Button
          className="text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-sm md:text-base"
          onClick={goToToday}
        >
          {t("today")}
        </Button>

        {onCaseTypeFilterChange && (
          <div className="max-w-[100px] md:max-w-[130px]">
            <DataTableFacetedFilter
              title={t("case_type")}
              options={cabinet_case_type.map((t: CaseType) => ({
                label: t.name,
                value: t.id.toString()
              }))}
              defaultSelected={selectedCaseTypes || []}
              onFacetChange={onCaseTypeFilterChange}
            />
          </div>
        )}

        {/* Lawyer filter */}
        {onAssignedToFilterChange && (
          <div className="flex items-center gap-1">
            <Popover open={lawyerOpen} onOpenChange={setLawyerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  role="combobox"
                  aria-expanded={lawyerOpen}
                  className={cn(
                    "h-9 max-w-[150px] md:max-w-[220px] justify-between border-dashed text-xs md:text-sm font-normal",
                    selectedLawyer && "border-solid border-primary bg-primary/5 text-primary"
                  )}
                >
                  <span className="flex min-w-0 items-center">
                    <UserCircle className="mr-1.5 h-3.5 w-3.5 shrink-0 opacity-60" />
                    <span className="truncate">
                      {selectedLawyer ? selectedLawyer.name : t("avocat", { defaultValue: "Avocat" })}
                    </span>
                  </span>
                  <span className="ml-1 flex shrink-0 items-center">
                    {selectedLawyer ? (
                      <X
                        className="h-3.5 w-3.5 cursor-pointer opacity-60 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAssignedToFilterChange(undefined);
                        }}
                      />
                    ) : (
                      <ChevronsUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[240px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={t("searchLawyer", { defaultValue: "Rechercher..." })}
                    value={lawyerSearch}
                    onValueChange={setLawyerSearch}
                    className="h-8 text-sm"
                  />
                  <CommandList>
                    {lawyersLoading ? (
                      <div className="py-4 text-center text-xs text-muted-foreground">{t("loading", { defaultValue: "Chargement..." })}</div>
                    ) : (
                      <>
                        <CommandEmpty>{t("noResults", { defaultValue: "Aucun résultat" })}</CommandEmpty>
                        <CommandGroup>
                          {/* Clear option */}
                          {selectedAssignedTo && (
                            <CommandItem
                              value="__clear__"
                              onSelect={() => {
                                onAssignedToFilterChange(undefined);
                                setLawyerOpen(false);
                              }}
                              className="text-xs text-muted-foreground italic"
                            >
                              <X className="mr-2 h-3 w-3 shrink-0" />
                              {t("allLawyers", { defaultValue: "Tous les avocats" })}
                            </CommandItem>
                          )}
                          {lawyers.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={user.id}
                              onSelect={(val) => {
                                onAssignedToFilterChange(val === selectedAssignedTo ? undefined : val);
                                setLawyerOpen(false);
                              }}
                              className="text-sm"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-3.5 w-3.5 shrink-0",
                                  selectedAssignedTo === user.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="truncate">{user.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* View toggles visible on mobile in this row */}
        <div className="md:hidden">
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(value) => {
              if (value) {
                setView(value as "month" | "week" | "day");
              }
            }}
            className="gap-0"
          >
            <ToggleGroupItem value="month" aria-label={t("monthView")} className="px-2">
              <CalendarIcon className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="week" aria-label={t("weekView")} className="px-2">
              <CalendarDays className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="day" aria-label={t("dayView")} className="px-2">
              <Clock className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Center: Navigation controls and date display */}
      <div className={`flex items-center justify-center gap-2 md:gap-4 ${flexDirection}`}>
        <Button
          variant="outline"
          size="icon"
          className="rounded-l-none h-9 w-9 md:h-10 md:w-10"
          onClick={
            view === "month"
              ? goToPreviousMonth
              : view === "week"
                ? goToPreviousWeek
                : goToPreviousDay
          }
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {view === "month" ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                className="text-base md:text-xl font-medium text-gray-800 dark:text-gray-200 hover:underline decoration-dotted min-w-[140px] md:min-w-[180px] text-center"
                aria-label={t("selectMonth")}
              >
                {monthDisplay}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] md:w-[320px]">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("year")}</span>
                  <Select
                    value={pickerYear.toString()}
                    onValueChange={(v) => setPickerYear(Number(v))}
                  >
                    <SelectTrigger className="w-[120px] md:w-[140px] justify-end">
                      <SelectValue placeholder={pickerYear.toString()} />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {monthNames.map((name, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-center whitespace-nowrap text-xs md:text-sm"
                      onClick={() => {
                        goToDate(new Date(pickerYear, idx, 1));
                        setOpen(false);
                      }}
                    >
                      {name}
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        ) : view === "week" ? (
          <h2 className="text-base md:text-xl font-medium text-gray-800 dark:text-gray-200 min-w-[140px] md:min-w-[180px] text-center">
            {(() => {
              const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
              const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

              // If same month, show: "18-24 November 2025"
              if (weekStart.getMonth() === weekEnd.getMonth()) {
                return `${format(weekStart, 'd', { locale: currentLocale })}-${format(weekEnd, 'd MMMM yyyy', { locale: currentLocale })}`;
              }
              // If different months but same year: "28 Nov - 4 Dec 2025"
              else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
                return `${format(weekStart, 'd MMM', { locale: currentLocale })} - ${format(weekEnd, 'd MMM yyyy', { locale: currentLocale })}`;
              }
              // If different years: "28 Dec 2024 - 4 Jan 2025"
              else {
                return `${format(weekStart, 'd MMM yyyy', { locale: currentLocale })} - ${format(weekEnd, 'd MMM yyyy', { locale: currentLocale })}`;
              }
            })()}
          </h2>
        ) : (
          <h2 className="text-base md:text-xl font-medium text-gray-800 dark:text-gray-200 min-w-[140px] md:min-w-[180px] text-center">
            {format(selectedDay, "EEEE, d MMMM yyyy", { locale: currentLocale })}
          </h2>
        )}

        <Button
          variant="outline"
          size="icon"
          className="rounded-r-none h-9 w-9 md:h-10 md:w-10"
          onClick={
            view === "month"
              ? goToNextMonth
              : view === "week"
                ? goToNextWeek
                : goToNextDay
          }
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Desktop-only view toggles with labels */}
      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(value) => {
          if (value) {
            setView(value as "month" | "week" | "day");
          }
        }}
        className="hidden md:flex"
      >
        <ToggleGroupItem value="month" aria-label={t("monthView")}>
          <CalendarIcon className="w-4 h-4 ml-2" />
          {t("month")}
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label={t("weekView")}>
          <CalendarDays className="w-4 h-4 ml-2" />
          {t("week")}
        </ToggleGroupItem>
        <ToggleGroupItem value="day" aria-label={t("dayView")}>
          <Clock className="w-4 h-4 ml-2" />
          {t("day")}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

